import type jsPDF from "jspdf"

/**
 * jsPDF 벡터 출력용 한글 폰트(Noto Sans KR) 등록 유틸.
 *
 * jsPDF 기본 폰트(Helvetica 등)에는 한글 글리프가 없어, 벡터 텍스트로 한글을
 * 출력하려면 TTF 폰트를 base64로 변환해 VFS(Virtual File System)에 등록해야 한다.
 *
 * - 여러 CDN 후보를 순서대로 시도(단일 URL 404 시 전체 PDF 생성이 실패하는 문제 방지)
 * - base64 로 변환하여 addFileToVFS + addFont 로 등록
 * - 다운로드/변환 비용이 크므로 모듈 레벨에서 1회만 수행하고 결과를 캐시
 */

export const KR_FONT = "NotoSansKR"

/**
 * jsPDF 는 woff/woff2 를 직접 임베드할 수 없으므로 반드시 TTF/OTF 바이너리를 사용한다.
 * CDN 경로는 저장소 구조 변경으로 사라질 수 있어(과거 fonts-archive 경로 404 발생)
 * 반드시 복수 후보를 두고 순차 폴백한다.
 */
// 중요: jsPDF 의 폰트 서브셋터(GlyfTable)는 가변 폰트(variable font, 예: "...[wght].ttf")를 파싱하지
// 못하고 "Cannot use 'in' operator to search for '0' in undefined" 오류로 PDF 생성 자체를 중단시킨다.
// 따라서 반드시 정적(static) 개별 굵기 TTF만 후보로 사용해야 한다.
// (@expo-google-fonts 패키지는 굵기별로 하위 폴더에 정적 TTF를 제공한다: "<Weight>/NotoSansKR_<Weight>.ttf")
const REGULAR_URL_CANDIDATES = [
  "https://cdn.jsdelivr.net/npm/@expo-google-fonts/noto-sans-kr@0.4.3/400Regular/NotoSansKR_400Regular.ttf",
  "https://unpkg.com/@expo-google-fonts/noto-sans-kr@0.4.3/400Regular/NotoSansKR_400Regular.ttf",
]

const BOLD_URL_CANDIDATES = [
  "https://cdn.jsdelivr.net/npm/@expo-google-fonts/noto-sans-kr@0.4.3/700Bold/NotoSansKR_700Bold.ttf",
  "https://unpkg.com/@expo-google-fonts/noto-sans-kr@0.4.3/700Bold/NotoSansKR_700Bold.ttf",
]

let registerPromise: Promise<void> | null = null
let cachedRegular: string | null = null
let cachedBold: string | null = null

/** 폰트 로드가 최종 실패했는지 여부. 실패 시 호출부가 라틴 폰트로 폴백할 수 있게 노출한다. */
let fontUnavailable = false

export function isKoreanFontAvailable(): boolean {
  return !fontUnavailable
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let binary = ""
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + chunk)) as unknown as number[]
    )
  }
  return btoa(binary)
}

async function fetchAsBase64(url: string): Promise<string> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`폰트 다운로드 실패: ${url} (${res.status})`)
  const buf = await res.arrayBuffer()
  // TTF 최소 크기 방어: HTML 오류 페이지가 200 으로 내려오는 경우를 걸러낸다.
  if (buf.byteLength < 10000) {
    throw new Error(`폰트 파일이 유효하지 않음: ${url} (${buf.byteLength} bytes)`)
  }
  return arrayBufferToBase64(buf)
}

/** 후보 URL 을 순서대로 시도하고, 처음으로 성공한 base64 를 반환한다. */
async function fetchFirstAvailable(urls: string[]): Promise<string | null> {
  for (const url of urls) {
    try {
      return await fetchAsBase64(url)
    } catch (err) {
      console.warn("[pdfKoreanFont] 폰트 후보 로드 실패, 다음 후보 시도:", err)
    }
  }
  return null
}

/**
 * 주어진 jsPDF 인스턴스에 한글 폰트를 등록한다.
 *
 * - 폰트 바이너리 로드는 전역 1회만 수행(Promise 캐시), 등록은 인스턴스별로 진행
 * - 모든 CDN 후보가 실패해도 예외를 던지지 않고, 라틴 기본 폰트로 폴백하여
 *   PDF 생성 자체는 계속 진행되도록 한다(한글은 깨질 수 있음).
 */
export async function ensureKoreanFont(pdf: jsPDF): Promise<void> {
  if (!registerPromise) {
    registerPromise = (async () => {
      const [regular, bold] = await Promise.all([
        fetchFirstAvailable(REGULAR_URL_CANDIDATES),
        fetchFirstAvailable(BOLD_URL_CANDIDATES),
      ])
      cachedRegular = regular
      // Bold 전용 폰트를 못 받으면 Regular 를 bold 슬롯에도 재사용한다.
      cachedBold = bold ?? regular
      fontUnavailable = !cachedRegular
      if (fontUnavailable) {
        console.warn(
          "[pdfKoreanFont] 한글 폰트를 불러오지 못했습니다. 기본 폰트로 출력합니다."
        )
      }
    })()
  }

  await registerPromise

  if (!cachedRegular) {
    // 폰트 없이도 PDF 생성은 계속되도록 기본 폰트로 폴백
    pdf.setFont("helvetica", "normal")
    return
  }

  try {
    pdf.addFileToVFS("NotoSansKR-Regular.ttf", cachedRegular)
    pdf.addFont("NotoSansKR-Regular.ttf", KR_FONT, "normal")

    if (cachedBold) {
      pdf.addFileToVFS("NotoSansKR-Bold.ttf", cachedBold)
      pdf.addFont("NotoSansKR-Bold.ttf", KR_FONT, "bold")
    }

    pdf.setFont(KR_FONT, "normal")
  } catch (err) {
    console.warn("[pdfKoreanFont] 폰트 등록 실패, 기본 폰트로 폴백:", err)
    fontUnavailable = true
    pdf.setFont("helvetica", "normal")
  }
}

/**
 * 한글 폰트 사용 가능 여부에 따라 안전한 폰트명을 반환한다.
 * 폰트 등록이 실패한 환경에서 `setFont(KR_FONT)` 호출 시 발생하는 예외를 방지한다.
 */
export function safeFontName(): string {
  return fontUnavailable ? "helvetica" : KR_FONT
}
